import React, { ReactElement, MouseEventHandler } from 'react';
import { Button as MantineButton, ButtonVariant } from '@mantine/core';
import { Tooltip } from 'pageFramework/uiControls/tooltips';

type ColorTypes = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

export interface ButtonProps {
	type?: 'button' | 'submit' | 'reset';
	color?: ColorTypes;
	icon?: ReactElement;
	disabled?: boolean;
	variant?: ButtonVariant;
	fullWidth?: boolean;
	requirePermission?: string;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	children?: React.ReactNode;
	tooltipLabel?: string;
}

const colorMap: Record<ColorTypes, string> = {
	'primary': 'blue',
	'secondary': 'gray',
	'success': 'green',
	'danger': 'red',
	'warning': 'yellow',
	'info': 'teal',
	'light': 'gray',
	'dark': 'dark',
};

function convertToMantineColor(color: ColorTypes): string {
	return colorMap[color];
}

export const Button: React.FC<React.PropsWithChildren<ButtonProps>> = (props) => {
	const {
		type = 'button',
		color = 'primary',
		icon,
		disabled = false,
		fullWidth = false,
		variant,
		onClick,
		children,
		tooltipLabel,
	} = props;

	const button = (
		<MantineButton
			type={type}
			fullWidth={fullWidth}
			color={convertToMantineColor(color)}
			disabled={disabled}
			onClick={onClick}
			variant={variant}
			className="button"
		>
			<div className="button-content">
				{icon && <span className="button-icon">{icon}</span>}
				{children && <span className="button-text">{children}</span>}
			</div>
		</MantineButton>
	);

	const buttonWithTooltip = tooltipLabel ? (
		<Tooltip value={tooltipLabel}>
			{button}
		</Tooltip>
	) : (
		button
	);

	return buttonWithTooltip;
}
